// frontend/src/infrastructure/api/HttpSyncApiGateway.ts
//
// Implementacja `ISyncApiGateway` oparta o `fetch` — jedyne miejsce, które zna
// szczegóły transportu HTTP dla synchronizacji offline.
//
// Wysyła pojedynczy wpis z lokalnej kolejki (Dexie) na uniwersalny endpoint
// backendu `POST /api/sync` z ciałem `{ entityType, action, payload }`. Po
// stronie serwera to prosty router po parze (entityType, action) — patrz
// backend-v2/src/presentation/controllers/SyncController.ts.
//
// Endpoint jest chroniony middleware `requireAuth`, więc dokładamy nagłówek
// `Authorization: Bearer <JWT>`. Token czytamy z tego samego, zaszyfrowanego
// wpisu localStorage co reszta dema (patrz `getToken` w `src/api.ts`).

import type { ISyncApiGateway } from '../../domain/gateways/ISyncApiGateway'
import { decryptFromStore } from '../../utils/secureStorage'

/** Domyślny endpoint kolejki synchronizacji po stronie backendu. */
export const SYNC_ENDPOINT = '/api/sync'

/**
 * Klucz localStorage z zaszyfrowanym JWT — ten sam, którego używa demo
 * (`LS_KEY_JWT` w `src/api.ts`). Trzymany lokalnie, żeby warstwa Clean
 * Architecture nie musiała importować modułu `api.ts` z jego efektami ubocznymi.
 */
const LS_KEY_JWT = 'pwa_jwt'

export class HttpSyncApiGateway implements ISyncApiGateway {
  /**
   * @param endpoint adres endpointu synchronizacji; wstrzykiwalny na potrzeby
   *   testów i różnych środowisk. Domyślnie {@link SYNC_ENDPOINT}.
   */
  constructor(private readonly endpoint: string = SYNC_ENDPOINT) {}

  async push(entityType: string, action: string, payload: unknown): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // `/api/sync` jest za `requireAuth` — bez tokenu backend odpowie 401.
    const token = await decryptFromStore(LS_KEY_JWT)
    if (token) headers['Authorization'] = `Bearer ${token}`

    let response: Response
    try {
      response = await fetch(this.endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ entityType, action, payload }),
      })
    } catch (error) {
      // Brak sieci / przerwane połączenie — `fetch` odrzuca się z `TypeError`.
      // Tłumaczymy to na błąd kontraktu `ISyncApiGateway` (reject), żeby
      // `OfflineSyncService` przerwał przetwarzanie kolejki i ponowił próbę
      // przy następnym powrocie połączenia.
      throw new Error(
        `[HttpSyncApiGateway] Brak łączności z ${this.endpoint} ` +
          `(${entityType}/${action}): ${String(error)}`,
      )
    }

    if (!response.ok) {
      // 4xx/5xx — serwer NIE przyjął operacji. Rzucamy, żeby wołający przerwał
      // kolejkę; wpis zostaje nienaruszony do kolejnej próby.
      throw new Error(
        `[HttpSyncApiGateway] Wysyłka odrzucona: HTTP ${response.status} ` +
          `(${entityType}/${action}).`,
      )
    }
  }
}
